import { CameraLayoutRepository, LayoutUpdateListener } from 'core/repositories/CameraLayoutRepository';
import { VehicleCameraLayout, equalLayout, MainScreenLayout, SubScreenLayout } from 'core/models/layout';
import { Vehicle } from 'core/models/vehicle';
import { createKeyedListenerManager } from 'utility/KeyedListenerManager';
import { MediaDevice } from 'core/models/mediaDevice';

const CAMERA_LAYOUT_SETTINGS_KEY = 'jp.tier4.drive.autoware.layout.camera';
const DISPLAY_LAYOUT_SETTINGS_KEY = 'jp.tier4.drive.autoware.layout.display';

enum EightCamerasPredefinedTags {
    FrontCenter = 'front-center',
    FrontLeft = 'front-left',
    FrontRight = 'front-right',
    RearMirror = 'rear-mirror',
    LeftMirror = 'left-mirror',
    RightMirror = 'right-mirror',
    FrontBelow = 'front-below',
    Cabin = 'cabin'
}

type VehicleLayoutDataV0 = {
    id: Vehicle.Id;
    layout: MainScreenLayout;
}

type AssignedCameraData = {
    tag?: MediaDevice.Tag;
    mirror?: boolean;
    upsidedown?: boolean;
    position?: MediaDevice.Tag; // legacy
}

type AssignedMicrophoneData = {
    tag?: MediaDevice.Tag;
}

type VehicleLayoutDataV1V2 = {
    id: Vehicle.Id;
    layout: {
        mainscreen: {
            center: AssignedCameraData[];
            top: AssignedCameraData[];
            left: AssignedCameraData[];
            right: AssignedCameraData[];
            microphone?: AssignedMicrophoneData;
        };
        subscreens: {
            center: AssignedCameraData[];
            top: AssignedCameraData[];
            bottom: AssignedCameraData[];
        }[];
    };
}

const defaultSubScreenLayout: SubScreenLayout = {
    top: [],
    center: [{ tag: Vehicle.PredefinedCameraPosition.Front }],
    bottom: [],
};

function v0ToV1V2(d: VehicleLayoutDataV0): VehicleLayoutDataV1V2 {
    return {
        id: d.id,
        layout: {
            mainscreen: d.layout,
            subscreens: [defaultSubScreenLayout, defaultSubScreenLayout]
        }
    };
}

function positionToTag<U, T extends { position?: U; tag?: U }>(obj: T): Omit<T, 'position'> & { tag: T['position'] } {
    return { ...obj, tag: obj.tag ?? obj.position };
}

function apply<T, U>(obj: T | undefined, f: (obj: T) => U) {
    return obj !== undefined ? f(obj) : undefined;
}

function v1V2ToV2(v1: VehicleLayoutDataV1V2): VehicleLayoutDataV2 {
    const { id, layout } = v1;
    return {
        id,
        layout: {
            mainscreen: {
                center: layout.mainscreen.center.map(positionToTag),
                top: layout.mainscreen.top.map(positionToTag),
                left: layout.mainscreen.left.map(positionToTag),
                right: layout.mainscreen.right.map(positionToTag),
                microphone: apply(layout.mainscreen.microphone, positionToTag),
            },
            subscreens: layout.subscreens.map(subscreen => ({
                center: subscreen.center.map(positionToTag),
                top: subscreen.top.map(positionToTag),
                bottom: subscreen.bottom.map(positionToTag),
            })),
        },
    };
}

type VehicleLayoutDataV2 = {
    id: Vehicle.Id;
    layout: VehicleCameraLayout;
}

type VehicleLayoutData = VehicleLayoutDataV2;

const _defaultLayout: VehicleCameraLayout = {
    mainscreen: {
        center: [{ tag: EightCamerasPredefinedTags.FrontLeft }, { tag: EightCamerasPredefinedTags.FrontCenter }, { tag: EightCamerasPredefinedTags.FrontRight }],
        top: [{ tag: EightCamerasPredefinedTags.Cabin }, { tag: EightCamerasPredefinedTags.RearMirror }, { tag: EightCamerasPredefinedTags.FrontBelow }],
        left: [{ tag: EightCamerasPredefinedTags.LeftMirror }],
        right: [{ tag: EightCamerasPredefinedTags.RightMirror },],
        microphone: {},
    },
    subscreens: [
        { top: [], center: [{ tag: EightCamerasPredefinedTags.FrontLeft }], bottom: [] },
        { top: [], center: [{ tag: EightCamerasPredefinedTags.FrontRight }], bottom: [] },
    ],
};

export class LocalStorageCameraLayoutRepository implements CameraLayoutRepository {
    defaultLayout = _defaultLayout;

    private entries = this.load();
    private readonly listenerManager = createKeyedListenerManager<Vehicle.Id, LayoutUpdateListener>();

    addListener(vehicleId: Vehicle.Id, listener: LayoutUpdateListener): void {
        this.listenerManager.addListener(vehicleId, listener);
    }

    removeListener(vehicleId: Vehicle.Id, listener: LayoutUpdateListener): void {
        this.listenerManager.removeListener(vehicleId, listener);
    }

    get(vehicleId: Vehicle.Id): VehicleCameraLayout {
        const layout_ = this.findEntry(vehicleId)?.layout;
        if (!layout_) return _defaultLayout;
        return { mainscreen: { microphone: {}, ...layout_.mainscreen }, subscreens: layout_.subscreens };
    }

    set(vehicleId: Vehicle.Id, layout: VehicleCameraLayout): void {
        const entry = this.findEntry(vehicleId);
        if (entry) {
            if (equalLayout(layout, { mainscreen: { microphone: {}, ...entry.layout.mainscreen }, subscreens: entry.layout.subscreens })) return;
            entry.layout = layout;
        } else {
            this.entries.push({ id: vehicleId, layout });
        }
        this.save(this.entries);
        this.listenerManager.forEach(vehicleId, listener => {
            listener(layout);
        });
    }

    private findEntry(vehicleId: Vehicle.Id) {
        return this.entries.find(entry => entry.id === vehicleId);
    }

    private load(): VehicleLayoutData[] {
        const newOnes = this.loadV1V2();
        const oldOnes = this.loadV0();

        return [
            ...oldOnes,
            ...newOnes,
        ];
    }

    private loadV1V2(): VehicleLayoutData[] {
        const raw = localStorage.getItem(DISPLAY_LAYOUT_SETTINGS_KEY);
        return (raw ? JSON.parse(raw) as VehicleLayoutDataV1V2[] : []).map(v1V2ToV2);
    }

    private loadV0(): VehicleLayoutDataV2[] {
        const raw = localStorage.getItem(CAMERA_LAYOUT_SETTINGS_KEY);
        return (raw ? JSON.parse(raw) as VehicleLayoutDataV0[] : []).map(v0ToV1V2).map(v1V2ToV2);
    }

    private save(entries: VehicleLayoutData[]) {
        localStorage.setItem(DISPLAY_LAYOUT_SETTINGS_KEY, JSON.stringify(entries));
    }
}
